import { useTranslation } from 'react-i18next';

/**
 * Hook to get date formatting options based on current locale
 * - English (en): Uses 12-hour format with AM/PM
 * - Other languages (fr, de, es, etc.): Uses 24-hour format
 */
export const useDateFormat = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const use12Hour = locale.startsWith('en');

  return {
    locale,
    use12Hour,
    /**
     * Format time only (e.g., "2:30 PM" or "14:30")
     */
    formatTime: (date: Date) => {
      return new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: use12Hour,
      }).format(date);
    },
    /**
     * Format date with time (e.g., "Jan 15, 2:30 PM" or "15 janv., 14:30")
     */
    formatDateTimeFull: (date: Date) => {
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: use12Hour,
      }).format(date);
    },
    /**
     * Get date-fns format string for the current locale
     * Returns: "hh:mm a" for English, "HH:mm" for others
     */
    getTimeFormatString: () => {
      return use12Hour ? 'hh:mm a' : 'HH:mm';
    },
    /**
     * Get date-fns format string with date and time
     * Returns: "LLL dd, y, hh:mm a" for English, "dd LLL y, HH:mm" for others
     */
    getDateTimeFormatString: () => {
      return use12Hour ? 'LLL dd, y, hh:mm a' : 'dd LLL y, HH:mm';
    },
  };
};
